import React, { useMemo } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { BiPlus } from "react-icons/bi";
import { FiTrash2 } from "react-icons/fi";

import useTimezoneDate from "../../../../../../hooks/useTimezoneDate";
import { Shipment, ShipmentContainerInfo } from "../../../../../../models/shipments";
import { ValueOf } from "../../../../../../types";
import { ShipmentDetails } from "../../../../../../util/enums";
import { Input, Select } from "../../../../../Form";
import { DefaultTooltip } from "../../../../../Generic/Tooltip";
import getShipmentOptions from "../../../../../Shipments/hooks/getShipmentOptions";
import { DetailsSectionsProps } from "../../../../../Shipments/types";
import { useBookingContext } from "../../../../context/Booking";
import { BookingObjects } from "../../../../util";
import { DetailsSectionTitle } from "../SectionTitle";
import useStyles from "./styles";
import { useAppState } from "Context/StateContext";

interface Options {
  value: string;
  label: string;
}
interface DetailsGeneralSectionProps extends DetailsSectionsProps {
  onUpdateAllShipment: (value: Shipment) => void;
}

export const DetailsGeneralSection = ({
  generateBaseFieldProps,
  onUpdateShipmentDetails,
  onUpdateAllShipment,
  viewOnly,
  data,
  fieldHasError,
  getErrorByField,
}: DetailsGeneralSectionProps) => {
  const { t: translate } = useTranslation();
  const { handleChangeOptions } = useBookingContext();
  const { isSupplierPortal } = useAppState();

  const classes = useStyles();
  const { getDateValueUtcTimezone } = useTimezoneDate();

  const DEFAULT_CONTAINER: ShipmentContainerInfo = {
    container: "",
    grossWeight: "",
    seal: "",
    size: "",
  };

  const onRemoveContainerItem = (index: number) => {
    if (!data || !data.containers || !data.containers.length) return;

    const containers = _.cloneDeep(data.containers);

    if (!containers[index]) return;

    containers.splice(index, 1);

    onUpdateAllShipment({
      containers,
    });

    if (handleChangeOptions) handleChangeOptions(BookingObjects.CONTAINERS);
  };

  const onChangeContainerItemValue = (
    index: number,
    attribute: keyof ShipmentContainerInfo,
    value: ValueOf<ShipmentContainerInfo>
  ) => {
    if (!data || !attribute) return;

    let containers = _.cloneDeep(data.containers);

    if (!containers) {
      containers = [];
    }

    containers[index] = {
      ...(containers[index] || {}),
      [attribute]: value,
    };

    onUpdateAllShipment({
      containers,
    });

    if (handleChangeOptions) handleChangeOptions(BookingObjects.CONTAINERS);
  };

  const { sizeOptions } = getShipmentOptions(false);

  const onAddNewContainerItem = () => {
    onUpdateAllShipment({
      containers: [...(data?.containers || []), DEFAULT_CONTAINER],
    });

    if (handleChangeOptions) handleChangeOptions(BookingObjects.CONTAINERS);
  };

  const isFieldRequired = useMemo(() => {
    if (data?.shipTerms === "CY/CY" && isSupplierPortal) return true;
    return false;
  }, [data, isSupplierPortal]);

  const renderContainerItemsForm = () => {
    const containers =
      data?.containers && data.containers?.length ? data?.containers : [DEFAULT_CONTAINER];

    return containers.map((containerData, index) => {
      if (!containerData) return null;
      const { container, grossWeight, seal, size } = containerData;

      return (
        <Grid container item spacing={2} key={index} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={3} lg={3}>
            <Input
              {...generateBaseFieldProps(ShipmentDetails.CONTAINER)}
              placeholder=""
              value={container || ""}
              label={`${translate(ShipmentDetails.CONTAINER)} #`}
              onChange={({ target }) =>
                onChangeContainerItemValue(index, ShipmentDetails.CONTAINER, target.value)
              }
              required={isFieldRequired}
              error={fieldHasError(`containers[${index}].container`) || fieldHasError("containers")}
              errorMessage={
                getErrorByField(`containers[${index}].container`) || getErrorByField("containers")
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <Select
              {...generateBaseFieldProps(ShipmentDetails.SIZE)}
              displayEmpty
              placeholder={translate("selectOne")}
              label={translate("containerSize")}
              value={size || ""}
              disabled={viewOnly}
              onChange={({ target }: SelectChangeEvent<any>) =>
                onChangeContainerItemValue(index, ShipmentDetails.SIZE, target.value)
              }
              data-cy={`containerSize-${index}`}
              required={isFieldRequired}
              error={fieldHasError(`containers[${index}].size`) || fieldHasError("containers")}
              errorMessage={
                getErrorByField(`containers[${index}].size`) || getErrorByField("containers")
              }
            >
              {sizeOptions.map((option: Options) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={3}>
            <Input
              {...generateBaseFieldProps(ShipmentDetails.SEAL)}
              value={seal || ""}
              label={`${translate(ShipmentDetails.SEAL)} #`}
              onChange={({ target }) =>
                onChangeContainerItemValue(index, ShipmentDetails.SEAL, target.value)
              }
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            lg={3}
            container
            direction="row"
            alignItems="flex-end"
            // spacing={viewOnly ? 0 : 2}
          >
            <Grid item xs={viewOnly ? 12 : 10} lg={viewOnly ? 12 : 10} xl={viewOnly ? 12 : 11}>
              <Input
                {...generateBaseFieldProps(ShipmentDetails.GROSS_WEIGHT)}
                type="number"
                min="0"
                placeholder={translate("inputWeight")}
                value={grossWeight || ""}
                onChange={({ target }) =>
                  onChangeContainerItemValue(index, ShipmentDetails.GROSS_WEIGHT, target.value)
                }
                className={!viewOnly ? classes.fieldWhenInEditCreateMode : ""}
              />
            </Grid>
            {!viewOnly && (
              <Grid item xs={2} lg={2} xl={1} className={classes.addNewContainerButton}>
                {index === 0 ? (
                  <DefaultTooltip title={translate("addNewContainer")}>
                    <div className={classes.containerActionIcon} onClick={onAddNewContainerItem}>
                      <BiPlus color="#308CDB" size={24} />
                    </div>
                  </DefaultTooltip>
                ) : (
                  <div
                    className={classes.containerActionIcon}
                    onClick={() => onRemoveContainerItem(index)}
                  >
                    <FiTrash2 color="#F97066" size={20} />
                  </div>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    });
  };

  return (
    <Grid container item spacing={2}>
      <DetailsSectionTitle title={translate("general")} />
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.CONTAINER_ID)}
            label={`${translate("shipmentNumber")} #`}
            placeholder={`${translate("inputShipment")} #`}
            value={data?.containerId}
            disabled
            onChange={(event) =>
              onUpdateShipmentDetails(ShipmentDetails.CONTAINER_ID, event.target.value)
            }
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.CARGO_READY_DATE)}
            type="date"
            value={getDateValueUtcTimezone(data?.cargoReadyDate)}
            onChange={(event) =>
              onUpdateShipmentDetails(
                ShipmentDetails.CARGO_READY_DATE,
                new Date(event.target.value).toISOString()
              )
            }
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.ASN)}
            value={data?.asn || ""}
            label={`${translate(ShipmentDetails.ASN)} #`}
            onChange={(event) => onUpdateShipmentDetails(ShipmentDetails.ASN, event.target.value)}
          />
        </Grid>
      </Grid>
      {renderContainerItemsForm()}
    </Grid>
  );
};
